import { useState, useEffect, useMemo, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { RelatedItem } from "@/types/oil";

export interface OilFromDB {
  id: string;
  oil_id: string;
  name: string;
  name_en: string;
  scientific_name: string;
  description: string;
  properties: string[];
  uses: string[];
  safety_level: "safe" | "caution" | "aggressive";
  safety_notes: string[];
  categories: string[]; // This will be populated from the junction table
  image_url: string | null;
}

// Interface compatible with the static data format - now using RelatedItem for rich data
export interface Oil {
  id: string;
  uuid: string; // Add UUID for junction table operations
  name: string;
  nameEn: string;
  scientificName: string;
  description: string;
  properties: RelatedItem[];
  uses: RelatedItem[];
  safetyLevel: "safe" | "caution" | "aggressive";
  safetyNotes: RelatedItem[];
  categories: RelatedItem[];
  imageUrl?: string;
}

interface RelatedItemFromDB {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  sort_order: number | null;
}

interface OilWithRelations {
  id: string;
  oil_id: string;
  name: string;
  name_en: string;
  scientific_name: string;
  description: string;
  properties: string[];
  uses: string[];
  safety_level: "safe" | "caution" | "aggressive";
  safety_notes: string[];
  image_url: string | null;
  oil_category_relations: {
    oil_categories: RelatedItemFromDB;
  }[];
  oil_property_relations: {
    oil_properties: RelatedItemFromDB;
  }[];
  oil_use_relations: {
    oil_uses: RelatedItemFromDB;
  }[];
  oil_safety_note_relations: {
    oil_safety_notes: RelatedItemFromDB;
  }[];
}

// Helper to convert DB item to RelatedItem
function toRelatedItem(item: RelatedItemFromDB): RelatedItem {
  return {
    id: item.id,
    name: item.name,
    description: item.description || undefined,
    icon: item.icon || undefined,
    sortOrder: item.sort_order ?? undefined,
  };
}

// Helper to convert legacy string array to RelatedItem array
function legacyToRelatedItems(items: string[]): RelatedItem[] {
  return items.map((name, index) => ({
    id: `legacy-${index}`,
    name,
  }));
}

// Helper to sort RelatedItems by sortOrder
const sortByOrder = (a: RelatedItem, b: RelatedItem) => 
  (a.sortOrder ?? 999) - (b.sortOrder ?? 999);

function mapDBToOil(dbOil: OilWithRelations): Oil {
  // Extract full data from the relations and sort by sortOrder
  const categories = (dbOil.oil_category_relations?.map(
    (rel) => toRelatedItem(rel.oil_categories)
  ) || []).sort(sortByOrder);
  
  const propertiesFromRelations = (dbOil.oil_property_relations?.map(
    (rel) => toRelatedItem(rel.oil_properties)
  ) || []).sort(sortByOrder);
  
  const usesFromRelations = (dbOil.oil_use_relations?.map(
    (rel) => toRelatedItem(rel.oil_uses)
  ) || []).sort(sortByOrder);
  
  const safetyNotesFromRelations = (dbOil.oil_safety_note_relations?.map(
    (rel) => toRelatedItem(rel.oil_safety_notes)
  ) || []).sort(sortByOrder);

  return {
    id: dbOil.oil_id,
    uuid: dbOil.id,
    name: dbOil.name,
    nameEn: dbOil.name_en,
    scientificName: dbOil.scientific_name,
    description: dbOil.description,
    // Use relations if available, fallback to legacy array fields
    properties: propertiesFromRelations.length > 0 ? propertiesFromRelations : legacyToRelatedItems(dbOil.properties),
    uses: usesFromRelations.length > 0 ? usesFromRelations : legacyToRelatedItems(dbOil.uses),
    safetyLevel: dbOil.safety_level,
    safetyNotes: safetyNotesFromRelations.length > 0 ? safetyNotesFromRelations : legacyToRelatedItems(dbOil.safety_notes),
    categories,
    imageUrl: dbOil.image_url || undefined,
  };
}

export function useOils() {
  const [oilsDB, setOilsDB] = useState<OilWithRelations[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchOils = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch oils with all relations
      const { data, error } = await supabase
        .from("oils")
        .select(`
          id,
          oil_id,
          name,
          name_en,
          scientific_name,
          description,
          properties,
          uses,
          safety_level,
          safety_notes,
          image_url,
          oil_category_relations(
            oil_categories(id, name, description, icon, sort_order)
          ),
          oil_property_relations(
            oil_properties(id, name, description, icon, sort_order)
          ),
          oil_use_relations(
            oil_uses(id, name, description, icon, sort_order)
          ),
          oil_safety_note_relations(
            oil_safety_notes(id, name, description, icon, sort_order)
          )
        `)
        .order("name", { ascending: true });

      if (error) throw error;

      setOilsDB((data as OilWithRelations[]) || []);
    } catch (err) {
      console.error("Error fetching oils:", err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOils();
  }, [fetchOils]);

  const oils = useMemo(() => oilsDB.map(mapDBToOil), [oilsDB]);

  const categories = useMemo(() => {
    const allCategories = new Set<string>();
    oils.forEach((oil) => {
      oil.categories.forEach((cat) => allCategories.add(cat.name));
    });
    return ["Todos", ...Array.from(allCategories).sort()];
  }, [oils]);

  function searchOils(query: string): Oil[] {
    const lowerQuery = query.toLowerCase();
    return oils.filter(
      (oil) =>
        oil.name.toLowerCase().includes(lowerQuery) ||
        oil.nameEn.toLowerCase().includes(lowerQuery) ||
        oil.scientificName.toLowerCase().includes(lowerQuery) ||
        oil.properties.some((p) => p.name.toLowerCase().includes(lowerQuery)) ||
        oil.categories.some((c) => c.name.toLowerCase().includes(lowerQuery))
    );
  }

  function getOilById(id: string): Oil | undefined {
    return oils.find((oil) => oil.id === id);
  }

  function getOilByUuid(uuid: string): Oil | undefined {
    return oils.find((oil) => oil.uuid === uuid);
  }

  return {
    oils,
    oilsDB,
    categories,
    loading,
    error,
    searchOils,
    getOilById,
    getOilByUuid,
    refetch: fetchOils,
  };
}
